import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Bell, BellOff, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PushNotificationToggle() {
  const { user } = useAuth();
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
    
    // Check if already subscribed
    if ('serviceWorker' in navigator && user) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.pushManager.getSubscription().then((subscription) => {
          setSubscribed(!!subscription);
        });
      });
    }
  }, [user]);

  const handleToggle = async () => {
    if (!user) return;
    setLoading(true);

    try {
      if (subscribed) {
        // Unsubscribe
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
        
        await fetch(`${API_URL}/api/push/unsubscribe`, {
          method: 'DELETE',
          credentials: 'include'
        });
        
        setSubscribed(false);
      } else {
        // Request permission and subscribe
        const perm = await Notification.requestPermission();
        setPermission(perm);
        
        if (perm === 'granted') {
          const registration = await navigator.serviceWorker.ready;
          
          // Subscribe to push (using a placeholder VAPID key - would need real one for production)
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(
              'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U'
            )
          });
          
          // Send to backend
          await fetch(`${API_URL}/api/push/subscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              endpoint: subscription.endpoint,
              keys: {
                p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
                auth: arrayBufferToBase64(subscription.getKey('auth'))
              }
            })
          });
          
          setSubscribed(true);
        }
      }
    } catch (err) {
      console.log('Push notification error:', err);
    }
    
    setLoading(false);
  };

  // Don't show if not logged in or notifications not supported
  if (!user || !('Notification' in window) || !('serviceWorker' in navigator)) {
    return null;
  }

  // Don't show if permission denied
  if (permission === 'denied') {
    return (
      <div 
        className="flex items-center gap-2 text-xs p-2 rounded-lg"
        style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#EF4444' }}
      >
        <BellOff className="w-4 h-4" />
        <span>Notifications blocked</span>
      </div>
    );
  }

  return (
    <button
      onClick={handleToggle}
      disabled={loading}
      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all hover:scale-105 disabled:opacity-50"
      style={{ 
        backgroundColor: subscribed ? 'var(--primary)' : 'var(--surface-highlight)',
        color: subscribed ? 'white' : 'var(--text-main)'
      }}
      data-testid="push-notification-toggle"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : subscribed ? (
        <Bell className="w-4 h-4" />
      ) : (
        <BellOff className="w-4 h-4" />
      )}
      <span>{subscribed ? 'Notifications On' : 'Get Notified'}</span>
    </button>
  );
}

// Utility functions
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}
